"use client";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactMapGL, { AttributionControl, MapLayerMouseEvent, MapRef, Marker, Popup, ViewState } from "react-map-gl";
import "./mapbox-overrides.css";
import { twMerge } from "tailwind-merge";
import { getMapboxTokenFromEnv } from "../../config/mapbox";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useColorMode } from "../../utils/ColorModeProvider";
import { Icon } from "../Icon";
import type { MapControl } from "./controls";
import { DEFAULT_CONTROLS, renderControlsByPosition } from "./controls";
import { type LayerFeature, type LayerSpec, renderLayers } from "./layers";
import { ensureMapboxCss } from "./loadMapboxCss";
import { MapLoadingOverlay } from "./MapLoadingOverlay";
import { MapPlaceholder } from "./MapPlaceholder";
import { DEFAULT_MAP_TYPE, getMapStyle, type MapType } from "./mapConfig";
import { UserLocationMarker } from "./UserLocationMarker";

/**
 * Ref handle for programmatic map control
 */
export interface InteractiveMapHandle {
  /**
   * Show a tooltip for a specific feature
   * @param featureId - The feature ID to show tooltip for
   * @param layerId - Optional layer ID to narrow the search
   */
  showTooltip: (featureId: string | number, layerId?: string) => void;
  /**
   * Hide the currently active tooltip
   */
  hideTooltip: () => void;
  /**
   * Get the underlying Mapbox map instance
   */
  getMap: () => MapRef | null;
}

export interface InteractiveMapProps {
  /**
   * Initial camera position
   */
  initialViewState?: Partial<ViewState>;
  /**
   * Controlled view state (for controlled component pattern)
   */
  viewState?: ViewState;
  /**
   * Callback when view state changes
   */
  onViewStateChange?: (viewState: ViewState) => void;
  /**
   * Enable/disable scroll zoom
   * @default true
   */
  scrollEnabled?: boolean;
  /**
   * Container CSS class
   */
  className?: string;
  /**
   * Preview mode - disables all interactions and shows overlay CTA
   * @default false
   */
  previewMode?: boolean;
  /**
   * CTA component to show in preview mode
   */
  previewModeCta?: React.ReactNode;
  /**
   * Click handler for preview mode
   */
  onPreviewModePress?: () => void;
  /**
   * Map type - the visual style of the map
   * Automatically adapts to light/dark mode via ColorModeProvider
   *
   * - "streets": Street map with roads and labels
   * - "satellite": Satellite imagery with street overlays
   * - "neutral": Minimal/clean map design
   *
   * @default "streets"
   */
  mapType?: MapType;
  /**
   * Layers to render on the map
   *
   * Use the layers API for clean, type-safe layer configuration:
   *
   * @example
   * ```tsx
   * import { layer } from '@texturehq/edges';
   *
   * <InteractiveMap
   *   layers={[
   *     layer.vector({
   *       id: 'feeders',
   *       tileset: 'mapbox://texture.feeders',
   *       sourceLayer: 'feeders',
   *       renderAs: 'line',
   *       style: { color: { token: 'brand-primary' } },
   *       z: 10
   *     })
   *   ]}
   * />
   * ```
   */
  layers?: LayerSpec[];
  /**
   * Mapbox access token
   * @default getMapboxTokenFromEnv()
   */
  mapboxAccessToken?: string;
  /**
   * Custom request transform function
   */
  transformRequest?: (url: string, resourceType?: string) => { url: string; headers?: Record<string, string> };
  /**
   * Show loading state overlay (for external data loading)
   * The map automatically shows a loading state while Mapbox initializes.
   * Use this prop if you need to show loading while fetching additional data.
   * @default false
   */
  isLoading?: boolean;
  /**
   * Callback fired when the map finishes loading
   * Called after Mapbox is fully initialized and initial tiles are loaded.
   * The map handles its own loading state internally, but you can use this
   * callback for analytics, initializing overlays, or other side effects.
   */
  onLoad?: () => void;
  /**
   * Map controls configuration
   *
   * Configure which controls appear on the map and their behavior.
   * - Pass an array of control configs for full customization
   * - Pass "default" or omit for sensible defaults (navigation with compass, geolocate, and reset zoom)
   * - Pass false or empty array to hide all controls
   *
   * The navigation control includes reset zoom by default, which automatically appears
   * when the user moves away from the initial view position.
   *
   * @default "default"
   *
   * @example
   * // Use defaults (includes reset zoom)
   * controls="default"
   *
   * @example
   * // Custom controls (reset zoom included by default)
   * controls={[
   *   { type: 'navigation', position: 'bottom-right' },
   *   { type: 'search', position: 'top-left', onSearch: handleSearch },
   *   { type: 'layers', position: 'bottom-left', onMapTypeChange: setMapType }
   * ]}
   *
   * @example
   * // Navigation without reset zoom
   * controls={[
   *   { type: 'navigation', position: 'bottom-right', showResetZoom: false }
   * ]}
   *
   * @example
   * // No controls
   * controls={false}
   */
  controls?: MapControl[] | "default" | false;
  /**
   * Raw Mapbox GL children (Source, Layer, Marker, etc.)
   * Use for advanced layer configurations not supported by the layers prop
   */
  children?: React.ReactNode;
  /**
   * Layer IDs that should be interactive (clickable/hoverable)
   * Used when rendering raw Mapbox children that need click/hover events
   */
  additionalInteractiveLayerIds?: string[];
  /**
   * Handler for map clicks on interactive layers (includes both layers prop and children)
   * Receives the click event with feature data
   */
  onMapClick?: (event: import("react-map-gl").MapLayerMouseEvent) => void;
  /**
   * Show user location as a blue dot marker (like Google/Apple Maps)
   * This only controls the visibility of the blue dot marker.
   * Location tracking is automatically started when the user clicks the geolocate button.
   * @default true
   */
  showUserLocation?: boolean;
  /**
   * Callback when user location changes
   */
  onUserLocationChange?: (location: { latitude: number; longitude: number; accuracy?: number } | null) => void;
  /**
   * Show a marker pin at the map center
   * Useful for displaying a central location alongside other layers
   * The marker appears at the initial view state center (or current center if controlled)
   * @default false
   */
  showMarker?: boolean;
  /**
   * Custom location for the center marker (overrides view state center)
   * Only used when showMarker is true
   */
  markerLocation?: { latitude: number; longitude: number };
}

export const InteractiveMap = React.forwardRef<InteractiveMapHandle, InteractiveMapProps>(
  (
    {
      initialViewState,
      viewState,
      onViewStateChange,
      scrollEnabled = true,
      className,
      previewMode = false,
      previewModeCta,
      onPreviewModePress,
      mapType = DEFAULT_MAP_TYPE,
      layers = [],
      mapboxAccessToken,
      transformRequest,
      isLoading = false,
      onLoad,
      controls = "default",
      showUserLocation = true,
      onUserLocationChange,
      children,
      additionalInteractiveLayerIds = [],
      onMapClick,
      showMarker = false,
      markerLocation,
    },
    ref
  ) => {
    // Lazy evaluation of token - only call getMapboxTokenFromEnv() if not provided
    const token = mapboxAccessToken ?? getMapboxTokenFromEnv();

    // Validate token - show placeholder if missing
    if (!token) {
      return (
        <div className={`relative ${className || ""}`} style={{ width: "100%", height: "100%" }}>
          <MapPlaceholder className="w-full h-full" message="Map unavailable" />
        </div>
      );
    }

    const { isDarkTheme } = useColorMode();
    const mapRef = useRef<MapRef | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [showLoadingState, setShowLoadingState] = useState(false);

    useEffect(() => {
      void ensureMapboxCss();
    }, []);

    // Track meaningful user interactions (not accidental scroll/wheel events)
    const [hasUserMovedMap, setHasUserMovedMap] = useState(false);
    const moveCountRef = useRef(0);

    // Tooltip state
    const [activeTooltip, setActiveTooltip] = useState<{
      feature: LayerFeature;
      content: React.ReactNode;
      bare?: boolean;
    } | null>(null);

    // User location state (controlled by geolocate button, displayed based on showUserLocation prop)
    const [userLocation, setUserLocation] = useState<{
      latitude: number;
      longitude: number;
      accuracy?: number;
    } | null>(null);
    const watchIdRef = useRef<number | null>(null);
    // Tracks the inner Mapbox `Map` instance we've already fired `onLoad`
    // for. The inner `<Map>` is keyed on `mapType-isDarkTheme` and remounts
    // on basemap/theme swaps; each remount destroys the previous map and
    // creates a new one. Consumers' `onLoad` callbacks need to fire again
    // on every new instance (so they can re-register icons, re-attach
    // listeners, etc.), but we still want to dedupe within a single
    // instance — e.g. React Strict Mode's double-mount can fire `onLoad`
    // twice against the same map. Gating by instance identity covers both.
    const onLoadFiredForMapRef = useRef<unknown>(null);

    // Track if user has clicked geolocate (to start watching position)
    // Use localStorage to remember across refreshes
    const [isTrackingLocation, setIsTrackingLocation] = useLocalStorage("interactiveMap.trackingLocation", false);

    // Track if we've done the initial permission check
    const [hasCheckedPermission, setHasCheckedPermission] = useState(false);

    // Check permission on mount - only enable tracking if permission is already granted
    useEffect(() => {
      if (!navigator.geolocation) {
        setHasCheckedPermission(true);
        return;
      }

      // If user previously enabled tracking, verify permission is still granted before resuming
      if (isTrackingLocation) {
        if (!navigator.permissions?.query) {
          // Can't check permission (Safari), assume it's OK and let watchPosition handle it
          setHasCheckedPermission(true);
          return;
        }

        // Check if permission is still granted
        navigator.permissions
          .query({ name: "geolocation" as PermissionName })
          .then((permissionStatus) => {
            if (permissionStatus.state !== "granted") {
              // Permission was revoked, stop tracking
              setIsTrackingLocation(false);
            }
            setHasCheckedPermission(true);
          })
          .catch(() => {
            // Permissions API not supported (Safari), proceed anyway
            setHasCheckedPermission(true);
          });
      } else {
        setHasCheckedPermission(true);
      }
    }, []); // Only run once on mount

    // Delay showing loading state to avoid flash for fast loads
    useEffect(() => {
      const timer = setTimeout(() => {
        if (!isMapLoaded) {
          setShowLoadingState(true);
        }
      }, 200); // 200ms delay before showing loading state

      return () => clearTimeout(timer);
    }, [isMapLoaded]);

    // Re-arm the user-onLoad gate on every inner-map remount.
    //
    // The inner `<Map>` is keyed on `${mapType}-${isDarkTheme}`, so
    // basemap and theme swaps destroy the current Mapbox Map and create
    // a new one. We need to fire the user's `onLoad` once for that new
    // instance so consumers can re-register icons, re-attach listeners,
    // etc. — clear `onLoadFiredForMapRef` so the next `onLoad` on the
    // new instance passes the identity gate.
    //
    // Deliberately do NOT reset `isMapLoaded` here. The 200ms loading-
    // overlay timer at line ~327 is gated on `!isMapLoaded`; resetting
    // it on every remount caused a visible "loading" overlay (or worse,
    // an apparent blank map until the new style finished loading) on
    // every basemap/theme swap. The new `<Map>`'s `onLoad` fires
    // reliably on remount in production (Strict Mode double-mount only
    // affects the very first mount, not key-driven remounts), so the
    // polling fallback isn't needed for swaps — and trading a transient
    // overlay for a non-issue isn't worth it.
    useEffect(() => {
      onLoadFiredForMapRef.current = null;
    }, [mapType, isDarkTheme]);

    // Fallback: poll mapRef.loaded() in case onLoad callback is missed
    // (e.g. React Strict Mode double-mount destroys first instance after it fires onLoad)
    useEffect(() => {
      if (isMapLoaded) return;

      const interval = setInterval(() => {
        if (mapRef.current?.loaded()) {
          setIsMapLoaded(true);
          setShowLoadingState(false);
          const currentMap = mapRef.current.getMap?.();
          if (currentMap && onLoadFiredForMapRef.current !== currentMap) {
            onLoadFiredForMapRef.current = currentMap;
            onLoad?.();
          }
          clearInterval(interval);
        }
      }, 200);

      return () => clearInterval(interval);
    }, [isMapLoaded, onLoad]);

    // Watch user location when tracking is enabled (via geolocate button)
    useEffect(() => {
      // Wait until we've checked permission before starting to watch
      if (!hasCheckedPermission) {
        return;
      }

      if (!isTrackingLocation || previewMode) {
        // Clear any existing watch
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
        // Don't clear userLocation here - keep the last known position
        return;
      }

      if (!navigator.geolocation) {
        console.warn("Geolocation is not supported by this browser.");
        return;
      }

      // Start watching position
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };
          setUserLocation(location);
          onUserLocationChange?.(location);
        },
        (error) => {
          console.warn("Geolocation error:", error.message);
          setUserLocation(null);
          onUserLocationChange?.(null);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 30000, // Cache for 30 seconds
          timeout: 30000, // Match maximumAge
        }
      );

      // Cleanup on unmount
      return () => {
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
      };
    }, [isTrackingLocation, previewMode, onUserLocationChange, hasCheckedPermission]);

    // Handle map load event. Fires once per inner-map instance — basemap
    // and theme swaps remount the inner `<Map>` (keyed on `mapType` and
    // `isDarkTheme`), so consumers see `onLoad` again with the new map
    // available via `mapRef.current.getMap()`.
    const handleMapLoad = useCallback(() => {
      setIsMapLoaded(true);
      setShowLoadingState(false);
      const currentMap = mapRef.current?.getMap?.();
      if (currentMap && onLoadFiredForMapRef.current !== currentMap) {
        onLoadFiredForMapRef.current = currentMap;
        onLoad?.();
      }
    }, [onLoad]);

    // Default reset zoom handler - flies back to initial view state and hides button
    const defaultResetZoom = useCallback(() => {
      if (mapRef.current && initialViewState) {
        // Hide the button immediately and reset move counter
        setHasUserMovedMap(false);
        moveCountRef.current = 0;

        mapRef.current.flyTo({
          center: [initialViewState.longitude || 0, initialViewState.latitude || 0],
          zoom: initialViewState.zoom || 0,
          bearing: initialViewState.bearing || 0,
          pitch: initialViewState.pitch || 0,
          duration: 1000,
        });
      }
    }, [initialViewState]);

    // Convert controls prop to effective controls array
    const effectiveControls: MapControl[] = useMemo(() => {
      if (controls === false || controls === null) {
        return [];
      }
      if (controls === "default" || controls === undefined) {
        return DEFAULT_CONTROLS;
      }
      return controls;
    }, [controls]);

    // Default geolocate handler - enables location tracking and jumps to location
    const defaultGeolocate = useCallback(() => {
      if (!navigator.geolocation) {
        console.warn("Geolocation is not supported by this browser.");
        return;
      }

      // Permission check happens on mount, so we just proceed with geolocation request

      // Enable tracking which will start watching position
      setIsTrackingLocation(true);

      // Get and jump to the user's location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (mapRef.current) {
            mapRef.current.jumpTo({
              center: [longitude, latitude],
              zoom: 15,
            });
          }
        },
        (error) => {
          console.warn("Geolocation error:", error.message);
          // If permission denied, stop tracking
          if (error.code === error.PERMISSION_DENIED) {
            setIsTrackingLocation(false);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }, []);

    // Render controls from array with context
    const renderedControls = useMemo(() => {
      if (previewMode) {
        return null;
      }

      // Inject default handlers into controls that don't have custom ones
      const controlsWithDefaults = effectiveControls.map((control) => {
        if (control.type === "navigation") {
          return {
            ...control,
            // Use custom handler if provided, otherwise use default
            onResetZoom: control.onResetZoom || defaultResetZoom,
            // Use the default geolocate handler (which enables tracking + flies to location)
            onGeolocateClick: control.onGeolocateClick || defaultGeolocate,
          };
        }
        return control;
      });

      return renderControlsByPosition(controlsWithDefaults, {
        mapRef,
        layers,
        // Show reset button only when user has moved the map
        showResetButton: hasUserMovedMap,
      });
    }, [effectiveControls, mapRef, previewMode, layers, defaultResetZoom, hasUserMovedMap, defaultGeolocate]);

    // Extract layer IDs that have event handlers or tooltips for interactivity
    const interactiveLayerIds = useMemo(() => {
      const layerIds: string[] = [];

      layers
        .filter(
          (layer) => layer.events?.onClick || layer.events?.onMouseEnter || layer.events?.onMouseLeave || layer.tooltip
        )
        .forEach((layer) => {
          // For clustered vector layers, add all sub-layer IDs
          if (layer.kind === "clusteredVector") {
            layerIds.push(`${layer.id}-clusters`);
            layerIds.push(`${layer.id}-points`);
            layerIds.push(`${layer.id}-selected-point`);
          } else {
            layerIds.push(layer.id);
          }
        });

      // Include additional interactive layer IDs from raw Mapbox children
      return [...layerIds, ...additionalInteractiveLayerIds];
    }, [layers, additionalInteractiveLayerIds]);

    // Create a map of layer IDs to their event handlers for quick lookup
    const layerEventHandlers = useMemo(() => {
      const handlers = new Map<string, NonNullable<LayerSpec["events"]>>();
      layers.forEach((layer) => {
        if (layer.events) {
          handlers.set(layer.id, layer.events);

          // For clustered vector layers, also register event handlers for all sub-layers
          if (layer.kind === "clusteredVector") {
            handlers.set(`${layer.id}-clusters`, layer.events);
            handlers.set(`${layer.id}-points`, layer.events);
            handlers.set(`${layer.id}-selected-point`, layer.events);
          }
        }
      });
      return handlers;
    }, [layers]);

    // Create a map of layer IDs to their tooltip configs
    const layerTooltips = useMemo(() => {
      const tooltips = new Map<string, NonNullable<LayerSpec["tooltip"]>>();
      layers.forEach((layer) => {
        if (layer.tooltip) {
          tooltips.set(layer.id, layer.tooltip);

          // For clustered vector layers, also register tooltips for all sub-layers
          if (layer.kind === "clusteredVector") {
            // Register the tooltip for all cluster-related layers
            tooltips.set(`${layer.id}-clusters`, layer.tooltip);
            tooltips.set(`${layer.id}-points`, layer.tooltip);
            tooltips.set(`${layer.id}-selected-point`, layer.tooltip);
          }
        }
      });
      return tooltips;
    }, [layers]);

    // Create a map of clustered layer specs for cluster-specific handling
    const clusteredLayers = useMemo(() => {
      const map = new Map<string, Extract<LayerSpec, { kind: "clusteredVector" }>>();
      layers.forEach((layer) => {
        if (layer.kind === "clusteredVector") {
          map.set(layer.id, layer);
          // Also map sub-layer IDs to the parent spec
          map.set(`${layer.id}-clusters`, layer);
          map.set(`${layer.id}-points`, layer);
          map.set(`${layer.id}-selected-point`, layer);
        }
      });
      return map;
    }, [layers]);

    // Compute marker location for center pin
    const markerCoords = useMemo(() => {
      if (!showMarker) return null;

      const longitude = markerLocation?.longitude ?? viewState?.longitude ?? initialViewState?.longitude;
      const latitude = markerLocation?.latitude ?? viewState?.latitude ?? initialViewState?.latitude;

      if (longitude === undefined || latitude === undefined) return null;

      return { longitude, latitude };
    }, [showMarker, markerLocation, viewState, initialViewState]);

    // Handle click events on interactive layers
    const handleLayerClick = useCallback(
      (event: MapLayerMouseEvent) => {
        if (previewMode) return;

        const feature = event.features?.[0];
        if (!feature || !feature.layer) return;

        const layerId = feature.layer.id;
        const handlers = layerEventHandlers.get(layerId);
        const tooltipConfig = layerTooltips.get(layerId);

        // Extract coordinates from feature geometry (prefer geometry over mouse position for accuracy)
        let coordinates: [number, number];
        if (feature.geometry.type === "Point") {
          coordinates = feature.geometry.coordinates as [number, number];
        } else {
          // For non-point geometries, fall back to mouse position
          coordinates = [event.lngLat.lng, event.lngLat.lat];
        }

        const layerFeature: LayerFeature = {
          // Mapbox doesn't always preserve GeoJSON feature IDs, so fall back to properties.id
          id: feature.id ?? feature.properties?.id,
          lngLat: coordinates,
          properties: feature.properties || {},
          layerId,
        };

        // Check if this is a clustered layer
        const clusteredLayerSpec = clusteredLayers.get(layerId);
        const isCluster = feature.properties?.cluster === true;

        // Handle cluster clicks with auto-zoom
        if (clusteredLayerSpec && isCluster) {
          const map = mapRef.current;
          if (map) {
            const currentZoom = map.getZoom();
            const newZoom = Math.min(currentZoom + 3, 18); // Zoom in by 3 levels, max 18

            map.flyTo({
              center: coordinates,
              zoom: newZoom,
              duration: 1000,
            });
          }
          return; // Don't call onClick handler for clusters
        }

        // Call onClick handler if exists (not for clusters)
        if (handlers?.onClick) {
          handlers.onClick(layerFeature);
        }

        // Show tooltip if configured for click trigger
        if (tooltipConfig && tooltipConfig.trigger === "click") {
          let content: React.ReactNode;

          if (clusteredLayerSpec && "cluster" in tooltipConfig) {
            // Clustered layer with cluster-specific tooltips
            if (tooltipConfig.point) {
              content = tooltipConfig.point(layerFeature);
            }
          } else if ("content" in tooltipConfig) {
            // Standard tooltip
            content =
              typeof tooltipConfig.content === "function" ? tooltipConfig.content(layerFeature) : tooltipConfig.content;
          }

          if (content) {
            setActiveTooltip({ feature: layerFeature, content, bare: tooltipConfig.bare });
          }
        }

        // Call onMapClick handler for raw Mapbox children layers
        if (onMapClick) {
          onMapClick(event);
        }
      },
      [layerEventHandlers, layerTooltips, previewMode, onMapClick, clusteredLayers, mapRef, interactiveLayerIds]
    );

    // Shared helper: resolve hover tooltip content for a mouse event
    const resolveHoverTooltip = useCallback(
      (event: MapLayerMouseEvent) => {
        const feature = event.features?.[0];
        if (!feature || !feature.layer) return null;

        const layerId = feature.layer.id;
        const tooltipConfig = layerTooltips.get(layerId);
        if (!tooltipConfig || (tooltipConfig.trigger !== "hover" && tooltipConfig.trigger !== undefined)) return null;

        // Extract coordinates from feature geometry (prefer geometry over mouse position for accuracy)
        let coordinates: [number, number];
        if (feature.geometry.type === "Point") {
          coordinates = feature.geometry.coordinates as [number, number];
        } else {
          coordinates = [event.lngLat.lng, event.lngLat.lat];
        }

        const layerFeature: LayerFeature = {
          id: feature.id ?? feature.properties?.id,
          lngLat: coordinates,
          properties: feature.properties || {},
          layerId,
        };

        let content: React.ReactNode;

        const clusteredLayerSpec = clusteredLayers.get(layerId);
        if (clusteredLayerSpec && "cluster" in tooltipConfig) {
          const isCluster = feature.properties?.cluster === true;
          if (isCluster && tooltipConfig.cluster) {
            const pointCount = feature.properties?.point_count || 0;
            content = tooltipConfig.cluster(pointCount, layerFeature);
          } else if (!isCluster && tooltipConfig.point) {
            content = tooltipConfig.point(layerFeature);
          } else {
            content = isCluster ? `${feature.properties?.point_count || 0} items` : null;
          }
        } else if ("content" in tooltipConfig) {
          content =
            typeof tooltipConfig.content === "function" ? tooltipConfig.content(layerFeature) : tooltipConfig.content;
        }

        if (content) {
          return { feature: layerFeature, content, bare: tooltipConfig.bare };
        }
        return null;
      },
      [layerTooltips, clusteredLayers]
    );

    // Handle mouse enter events on interactive layers
    const handleLayerMouseEnter = useCallback(
      (event: MapLayerMouseEvent) => {
        if (previewMode) return;

        const feature = event.features?.[0];
        if (!feature || !feature.layer) return;

        const layerId = feature.layer.id;
        const handlers = layerEventHandlers.get(layerId);

        // Call onMouseEnter handler if exists
        if (handlers?.onMouseEnter) {
          // Extract coordinates from feature geometry
          let coordinates: [number, number];
          if (feature.geometry.type === "Point") {
            coordinates = feature.geometry.coordinates as [number, number];
          } else {
            coordinates = [event.lngLat.lng, event.lngLat.lat];
          }

          const layerFeature: LayerFeature = {
            id: feature.id ?? feature.properties?.id,
            lngLat: coordinates,
            properties: feature.properties || {},
            layerId,
          };
          handlers.onMouseEnter(layerFeature);
        }

        // Show tooltip if configured for hover trigger
        const tooltip = resolveHoverTooltip(event);
        if (tooltip) {
          setActiveTooltip(tooltip);
        }

        // Set cursor to pointer
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = "pointer";
        }
      },
      [layerEventHandlers, previewMode, mapRef, resolveHoverTooltip]
    );

    // Handle mouse move over interactive layers — keeps hover tooltips
    // updated as the cursor moves between features within the same layer
    // (onMouseEnter alone doesn't re-fire for adjacent features in the same layer)
    const handleLayerMouseMove = useCallback(
      (event: MapLayerMouseEvent) => {
        if (previewMode) return;

        const feature = event.features?.[0];
        if (!feature || !feature.layer) return;

        const tooltip = resolveHoverTooltip(event);
        if (tooltip) {
          // Skip update if we're already showing a tooltip for the same feature
          setActiveTooltip((prev) => {
            if (prev && prev.feature.layerId === tooltip.feature.layerId && prev.feature.id === tooltip.feature.id) {
              return prev;
            }
            return tooltip;
          });
          return;
        }

        // No hover tooltip resolved — clear any existing hover-triggered tooltip
        // but preserve click-triggered tooltips
        setActiveTooltip((prev) => {
          if (!prev) return prev;
          const prevTooltipConfig = layerTooltips.get(prev.feature.layerId);
          const isHoverTooltip =
            prevTooltipConfig && (prevTooltipConfig.trigger === "hover" || prevTooltipConfig.trigger === undefined);
          return isHoverTooltip ? null : prev;
        });
      },
      [previewMode, resolveHoverTooltip, layerTooltips]
    );

    // Handle mouse leave events on interactive layers
    const handleLayerMouseLeave = useCallback(() => {
      if (previewMode) return;

      // Call onMouseLeave for any layer that has it
      layerEventHandlers.forEach((handlers) => {
        handlers.onMouseLeave?.();
      });

      // Hide tooltip (only for hover-triggered tooltips)
      if (activeTooltip) {
        const layerId = activeTooltip.feature.layerId;

        // Find the parent layer (handle sub-layers like sites-points, sites-clusters)
        let tooltipLayer = layers.find((l) => l.id === layerId);

        // If not found, check if this is a sub-layer of a clustered layer
        if (!tooltipLayer) {
          // Extract base layer id (e.g., "sites" from "sites-points")
          const baseLayerId = layerId.replace(
            /-(?:clusters|points|selected-point|cluster-shadows|points-shadow|cluster-count)$/,
            ""
          );
          tooltipLayer = layers.find((l) => l.id === baseLayerId);
        }

        // Hide tooltip if it's hover-triggered (or if trigger is not specified, which defaults to hover)
        if (tooltipLayer?.tooltip) {
          const trigger = tooltipLayer.tooltip.trigger || "hover";
          if (trigger === "hover") {
            setActiveTooltip(null);
          }
        }
      }

      // Reset cursor
      if (mapRef.current) {
        mapRef.current.getCanvas().style.cursor = "";
      }
    }, [layerEventHandlers, previewMode, mapRef, activeTooltip, layers]);

    // Helper function to find a feature by ID across all layers
    const findFeatureById = useCallback(
      (featureId: string | number, targetLayerId?: string): LayerFeature | null => {
        // Search through layers to find the feature
        for (const layer of layers) {
          // Skip layers without tooltips
          if (!layer.tooltip) continue;

          // If targetLayerId is specified, only search that layer
          if (targetLayerId && layer.id !== targetLayerId) continue;

          // Handle different layer types
          if (layer.kind === "geojson") {
            // Handle both FeatureCollection and single Feature
            if (typeof layer.data === "string") {
              // URL-based GeoJSON can't be searched client-side
              continue;
            }

            const data = layer.data as GeoJSON.FeatureCollection | GeoJSON.Feature;
            const features = data.type === "FeatureCollection" ? data.features : [data];

            const feature = features.find((f) => f.id === featureId);
            if (feature && feature.geometry.type === "Point") {
              return {
                id: feature.id,
                lngLat: feature.geometry.coordinates as [number, number],
                properties: feature.properties || {},
                layerId: layer.id,
              };
            }
          } else if (layer.kind === "customPins") {
            const dataItem = layer.data.find((item: Record<string, unknown>) => {
              // Try common ID properties
              return item.id === featureId || item._id === featureId || item.key === featureId;
            });
            if (dataItem) {
              return {
                id: featureId,
                lngLat: layer.getPosition(dataItem),
                properties: dataItem,
                layerId: layer.id,
              };
            }
          }
          // Note: Vector layers can't be searched client-side as they're rendered by Mapbox
        }
        return null;
      },
      [layers]
    );

    // Expose imperative handle for external tooltip control
    useImperativeHandle(
      ref,
      () => ({
        showTooltip: (featureId: string | number, layerId?: string) => {
          const feature = findFeatureById(featureId, layerId);
          if (!feature) {
            return;
          }

          const layer = layers.find((l) => l.id === feature.layerId);
          if (!layer?.tooltip) {
            return;
          }

          // Only standard tooltips are accessible via public API
          // Clustered tooltips are handled internally in mouse/click handlers
          if ("content" in layer.tooltip) {
            const content =
              typeof layer.tooltip.content === "function" ? layer.tooltip.content(feature) : layer.tooltip.content;

            setActiveTooltip({ feature, content, bare: layer.tooltip.bare });
          }
        },
        hideTooltip: () => {
          setActiveTooltip(null);
        },
        getMap: () => mapRef.current,
      }),
      [findFeatureById, layers]
    );

    // Default transform request if not provided
    const effectiveTransformRequest = (url: string, resourceType?: string) => {
      // If a custom transform request is provided, use it
      if (transformRequest) {
        return transformRequest(url, resourceType);
      }
      // Otherwise just return the URL unchanged
      return { url };
    };

    // Determine if we should show loading overlay
    const showLoadingOverlay = isLoading || showLoadingState;

    return (
      <div
        ref={containerRef}
        className={twMerge("relative flex h-full w-full overflow-hidden", previewMode && "cursor-pointer", className)}
        onClick={previewMode ? onPreviewModePress : undefined}
      >
        <div style={{ flex: 1 }}>
          <ReactMapGL
            ref={mapRef}
            key={`map-${mapType}-${isDarkTheme}`}
            initialViewState={initialViewState}
            {...(viewState && viewState)}
            style={{ width: "100%", height: "100%" }}
            mapStyle={getMapStyle(mapType, isDarkTheme)}
            styleDiffing={false}
            mapboxAccessToken={token}
            transformRequest={effectiveTransformRequest}
            onLoad={handleMapLoad}
            onMove={
              previewMode
                ? undefined
                : (evt) => {
                    // Track user-initiated movements (not programmatic ones from layer loading, etc)
                    // CRITICAL: Only track AFTER map is loaded to prevent false positives during initialization
                    if (evt.originalEvent && isMapLoaded) {
                      const eventType = evt.originalEvent.type;

                      // Check if map has actually moved from initial position
                      const latDiff = Math.abs((evt.viewState.latitude ?? 0) - (initialViewState?.latitude ?? 0));
                      const lngDiff = Math.abs((evt.viewState.longitude ?? 0) - (initialViewState?.longitude ?? 0));
                      const zoomDiff = Math.abs((evt.viewState.zoom ?? 0) - (initialViewState?.zoom ?? 0));

                      const hasMovedFromInitial =
                        initialViewState && (latDiff > 0.0001 || lngDiff > 0.0001 || zoomDiff > 0.1);

                      // Only track if moved from initial position AND it's a user interaction
                      if (hasMovedFromInitial) {
                        // Require deliberate interaction, not accidental wheel/scroll events
                        // Count moves and only show button after sustained interaction OR drag/touch
                        if (eventType === "mousemove" || eventType === "touchmove" || eventType === "pointermove") {
                          // Drag interaction - show immediately
                          setHasUserMovedMap(true);
                        } else if (eventType === "wheel" || eventType === "mousewheel") {
                          // Zoom interaction - require multiple events to avoid accidental scrolls
                          moveCountRef.current += 1;
                          if (moveCountRef.current >= 3) {
                            setHasUserMovedMap(true);
                          }
                        }
                      }
                    }
                    // Always call the parent callback if provided
                    onViewStateChange?.(evt.viewState);
                  }
            }
            onClick={previewMode ? undefined : handleLayerClick}
            onMouseMove={previewMode ? undefined : handleLayerMouseMove}
            onMouseEnter={previewMode ? undefined : handleLayerMouseEnter}
            onMouseLeave={previewMode ? undefined : handleLayerMouseLeave}
            interactiveLayerIds={previewMode ? [] : interactiveLayerIds}
            scrollZoom={previewMode ? false : scrollEnabled}
            dragPan={!previewMode}
            dragRotate={!previewMode}
            doubleClickZoom={!previewMode}
            interactive={!previewMode}
            attributionControl={false}
          >
            {/* Attribution in top-left corner */}
            <AttributionControl position="top-left" />

            {/* Render all controls from controls array */}
            {renderedControls}

            {/* Render custom layers from new layers API */}
            {renderLayers(layers, mapType)}

            {/* Render raw Mapbox GL children (Source, Layer, etc.) */}
            {children}

            {/* Show marker at center if enabled */}
            {markerCoords && (
              <Marker longitude={markerCoords.longitude} latitude={markerCoords.latitude} anchor="bottom">
                <Icon name="MapPin" size={36} className="text-map-marker-default" weight="fill" />
              </Marker>
            )}

            {/* Render user location marker (only if showUserLocation is true and we have a location) */}
            {showUserLocation && userLocation && (
              <UserLocationMarker
                longitude={userLocation.longitude}
                latitude={userLocation.latitude}
                accuracy={userLocation.accuracy}
              />
            )}

            {/* Render tooltip if active. `bare` strips the default popup
                chrome (padding/background/border/shadow/tip) so a layer can
                bring its own card styling — see `.map-tooltip-bare` rules
                in mapbox-overrides.css. */}
            {activeTooltip && (
              <Popup
                longitude={activeTooltip.feature.lngLat[0]}
                latitude={activeTooltip.feature.lngLat[1]}
                offset={15}
                closeButton={false}
                closeOnClick={false}
                className={activeTooltip.bare ? "map-tooltip map-tooltip-bare" : "map-tooltip"}
                maxWidth="none"
              >
                {activeTooltip.content}
              </Popup>
            )}
          </ReactMapGL>

          {/* Loading overlay */}
          {showLoadingOverlay && <MapLoadingOverlay />}

          {previewMode && previewModeCta && <div className="absolute bottom-8 right-8 z-10">{previewModeCta}</div>}
        </div>
      </div>
    );
  }
);

InteractiveMap.displayName = "InteractiveMap";
